import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { GetDoc } from "./StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue, getBlockedFieldDisplayValue } from "@coreapp/utils/FormatFieldValue";
import { QObjDBClass } from "@coreapp/CoreApp/QObjDBClass";
import { QXDoc } from "@coreapp/CoreApp/QObjDB";
import { QIWizData } from "@coreapp/CoreApp/QIWizData";

const PositionIDField = "PositionID";

export class BoxChip {
  employeesName?: string;
  title: string;
  entityID: string;
  holders?: any[];
  isFreeHolder: boolean;
  private isPosition: boolean;

  static HOLDERS_NAME = "__HOLDERS_NAMES__";
  static FREE_HOLDER_LABEL = "__UNASSIGNED_EMPLOYEE__";
  static ASSIGNED_OUT_OF_SCOPE_LABEL = "__ASSIGNED_OUT_OF_SCOPE__";
  static HOLDERS_FIELD = "__HOLDERS__";
  static RECENT_SEARCH_IS_POSITION = "__IS_POSITION__";

  constructor(
    employeesName: string | undefined,
    title: string,
    entityID: string,
    isPosition: boolean = false,
    isFreeHolder: boolean = false
  ) {
    this.employeesName = sanitizeBlockedValue(employeesName);
    this.title = sanitizeBlockedValue(title);
    this.entityID = sanitizeBlockedValue(entityID);
    this.isPosition = isPosition;
    this.isFreeHolder = isFreeHolder;
  }

  static FromLookupResult(data: any, chartConfig: any) {
    if (!chartConfig?.fields) return new BoxChip("", "", "", false);

    const isPosition = data[this.HOLDERS_FIELD] !== undefined;

    const nameField = chartConfig?.fields["Name"];
    const titleField = isPosition
      ? chartConfig?.fields["Position Title"]
      : chartConfig?.fields["Title"];
    const entityIDField = isPosition
      ? chartConfig?.fields["PositionID"]
      : chartConfig?.fields["PersonID"];

    let name;
    const entityID = data[entityIDField];
    const title = data[titleField];

    if (isPosition) {
      name = data[this.HOLDERS_FIELD]
        .map((h: any) => sanitizeBlockedValue(h[nameField]))
        .filter((n: any) => n != null)
        .join(", ");
    } else {
      name = data[nameField];
    }

    return new BoxChip(name, title, entityID, isPosition);
  }

  static FromSearchResult(data: any, isRecentSearch: boolean = false, doc: QXDoc | null = null) {
    let entityID, employeesNames, title;
    const freeHolder =
      Boolean(data[this.FREE_HOLDER_LABEL]) || Boolean(data[this.ASSIGNED_OUT_OF_SCOPE_LABEL]);
    const isPosition =
      data[this.HOLDERS_FIELD] !== undefined ||
      (isRecentSearch && Boolean(data[this.RECENT_SEARCH_IS_POSITION]));
    const resolvedDoc = doc ?? GetDoc();
    const boxClass = resolvedDoc?.GetObjDB()?.GetClassByName("Box");
    const positionClass = resolvedDoc?.GetObjDB()?.GetClassByName("Position");
    const nameField = boxClass?.GetFieldDefByID("Name")?.GetFieldName();
    const mapping = this.SearchMapping(isPosition && !freeHolder ? positionClass : boxClass);

    if (isPosition && !freeHolder) {
      employeesNames = isRecentSearch
        ? sanitizeBlockedValue(data[this.HOLDERS_NAME])
        : data[this.HOLDERS_FIELD].map((h: any) => sanitizeBlockedValue(h[nameField])).join(", ");
      title = data[mapping.Title];
      entityID = data[mapping.EntityID];
    } else {
      title = data[mapping.Title];
      employeesNames = data[mapping.Name];
      entityID = data[mapping.EntityID];
    }

    return new BoxChip(employeesNames, title, entityID, isPosition, freeHolder);
  }

  static FromMappedObject(data: any, mapping: any, isPosition: boolean) {
    const title = data[mapping.Title];
    const entityID = data[mapping.EntityID];

    const name = data[mapping.Name];
    const employeesName = data[this.HOLDERS_FIELD]
      ?.map((h: any) => sanitizeBlockedValue(h[mapping.Name]))
      .join(", ");

    return new BoxChip(isPosition ? employeesName : name, title, entityID, isPosition);
  }

  static FromCohead(data: any) {
    const name = data["Name"];
    const title = data["Title"];
    const entityID = data["PersonID"];
    return new BoxChip(name, title, entityID);
  }

  static FromObjDBObject(data: QObjDBObject) {
    return data.IsPositionObj()
      ? this.FromPositionObjDBObject(data)
      : this.FromEmployeeObjDBObject(data);
  }

  static FromPositionObjDBObject(data: QObjDBObject) {
    let employeesName = data
      .GetHolderList()
      ?.map((h: QObjDBObject) => sanitizeBlockedValue(h.SmartGetFieldValue("Name")))
      .join(", ");
    const title = data?.SmartGetFieldValue("Position Title") as string;
    const positionID = data?.SmartGetFieldValue(PositionIDField) as string;

    if (employeesName == "<No Holder>") {
      employeesName = appstr("Vacant");
    }

    return new BoxChip(employeesName, title, positionID, true);
  }

  static FromEmployeeObjDBObject(data: QObjDBObject) {
    const name = data?.SmartGetFieldValue("Name") as string;
    const title =
      (data?.SmartGetFieldValue("Title") as string) ||
      (data?.SmartGetFieldValue("aTitle") as string);
    const personID = data?.GetObjectRecordID() as string;

    return new BoxChip(name, title, personID);
  }

  static SearchMapping(objClass?: Nullable<QObjDBClass>) {
    if (!objClass) {
      return {
        EntityID: null,
        Name: null,
        Title: null
      };
    }

    if (objClass.GetClassName() === "Position") {
      return {
        EntityID: objClass.GetFieldDefByID("PositionID")?.GetFieldName(),
        Name: null,
        Title: objClass.GetFieldDefByID("Position Title")?.GetFieldName()
      };
    } else {
      return {
        EntityID: objClass.GetFieldDefByID("PersonID")?.GetFieldName(),
        Name: objClass.GetFieldDefByID("Name")?.GetFieldName(),
        Title: objClass.GetFieldDefByID("Title")?.GetFieldName()
      };
    }
  }

  static topBoxLabelFromDoc(curDoc: QXDoc, iWiz: QIWizData) {
    if (iWiz.IsCoHeadChart()) {
      // Might want to change this if we have position co-heads
      const boxClass = curDoc.GetObjDB()?.GetClassByName("Box");
      const topBoxes = iWiz.GetTopBoxIDs();

      return topBoxes
        .map((topBox: string) => {
          const boxObj = boxClass?.FindObject(boxClass.GetRecordIDFieldDef(), topBox);
          if (!boxObj) return "";

          const boxChip = BoxChip.FromObjDBObject(boxObj);
          return boxChip.crumbLabel();
        })
        .join(", ");
    }

    return curDoc.GetRootObj() ? BoxChip.FromObjDBObject(curDoc.GetRootObj()).crumbLabel() : null;
  }

  label() {
    const blocked = getBlockedFieldDisplayValue();
    if (this.isPosition) {
      const holdersStr = this.employeesName || appstr("Vacant");
      const title = this.title ?? `<${appstr("None")}>`;
      const id = this.entityID ?? `<${appstr("None")}>`;
      if (holdersStr === blocked) {
        return `${title}: ${id} ${holdersStr}`;
      }
      return `${title}: ${id} (${holdersStr})`;
    } else if (this.isFreeHolder) {
      return `${this.employeesName}`;
    } else {
      const name = this.employeesName ?? `<${appstr("None")}>`;
      const title = this.title ?? `<${appstr("None")}>`;
      if (title === blocked || name === blocked) {
        return `${name} ${title}`;
      }
      return `${name} (${title})`;
    }
  }

  crumbLabel() {
    const blocked = getBlockedFieldDisplayValue();
    if (this.isPosition) {
      let holdersStr = this.employeesName || appstr("Vacant");
      // Since the empty positions have a dummy box with the tag <No Holder>, we need to replace it with the appstr("Vacant") string
      // It's commented as temporary, so this might need to be removed in the future.
      if (holdersStr === "<No Holder>") {
        holdersStr = appstr("Vacant");
      }
      const title = this.title ?? `<${appstr("None")}>`;
      const id = this.entityID ?? `<${appstr("None")}>`;
      if (holdersStr === blocked) {
        return `${title}: ${id} ${holdersStr}`;
      }
      return `${title}: ${id} (${holdersStr})`;
    } else {
      const name = this.employeesName ?? `<${appstr("None")}>`;
      const title = this.title ?? `<${appstr("None")}>`;
      if (title === blocked || name === blocked) {
        return `${name} ${title}`;
      }
      return `${name} (${title})`;
    }
  }

  changesLabel() {
    if (this.isPosition) {
      return `${this.title ?? ""}: ${this.entityID ?? ""}`;
    } else {
      return `${this.employeesName ?? ""}: ${this.entityID ?? ""}`;
    }
  }

  getValue() {
    return this.entityID;
  }

  get chipName() {
    if (this.isPosition) {
      return this.title || "";
    } else {
      return this.employeesName || "";
    }
  }
}
